import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ProgressBar } from "@/components/Badges";
import { ChevronRight } from "lucide-react";

const fmt = (v) => `${Math.round(Number(v || 0) * 100)}%`;

export default function RegionDetail() {
  const { regionId } = useParams();
  const [countries, setCountries] = useState([]);
  const [region, setRegion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/regions/${regionId}/countries`),
      api.get(`/regions`).then((r) => r.data.find((x) => x.id === regionId)),
    ])
      .then(([cs, r]) => { setCountries(cs.data); setRegion(r); })
      .finally(() => setLoading(false));
  }, [regionId]);

  if (loading) return <div className="p-10 mono text-xs text-[color:var(--ink-2)]">Loading…</div>;

  return (
    <div className="max-w-[1600px] mx-auto px-10 py-10 fade-in-up" data-testid="region-detail-page">
      <nav className="mono text-[11px] uppercase tracking-widest text-[color:var(--ink-2)] mb-4">
        <Link to="/regions" className="hover:underline">Regions</Link>
        <span className="mx-2">/</span>
        <span className="text-[color:var(--ink)]">{region?.name}</span>
      </nav>

      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <h1 className="serif text-6xl tracking-tight leading-none">{region?.name}</h1>
          <p className="text-sm text-[color:var(--ink-2)] mt-3">
            {region?.projects_count} language projects across {region?.countries_count} countries
          </p>
        </div>
        <div className="text-right">
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-1">
            Regional Average
          </div>
          <div className="serif text-5xl tracking-tight">{fmt(region?.avg_progress)}</div>
        </div>
      </div>

      <div className="hairline bg-white overflow-hidden">
        <table className="w-full data-table" data-testid="countries-table">
          <thead>
            <tr>
              <th>Country</th>
              <th className="text-right">Clusters</th>
              <th className="text-right">Projects</th>
              <th>Avg Progress</th>
              <th className="text-right">On Track</th>
              <th className="text-right">Behind</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {countries.map((c) => (
              <tr
                key={c.id}
                data-testid={`country-row-${c.name}`}
                className="cursor-pointer"
                onClick={() => window.location.assign(`/countries/${c.id}`)}
              >
                <td>
                  <Link to={`/countries/${c.id}`} className="serif text-xl tracking-tight hover:underline">
                    {c.name}
                  </Link>
                </td>
                <td className="text-right mono">{c.clusters_count}</td>
                <td className="text-right mono">{c.projects_count}</td>
                <td className="w-64">
                  <div className="flex items-center gap-3">
                    <div className="flex-1"><ProgressBar value={c.avg_progress} /></div>
                    <span className="mono text-xs w-10 text-right">{fmt(c.avg_progress)}</span>
                  </div>
                </td>
                <td className="text-right mono text-[color:var(--on-track)]">{c.on_track_count}</td>
                <td className="text-right mono text-[color:var(--behind)]">{c.behind_count}</td>
                <td className="text-right">
                  <ChevronRight size={14} className="inline text-[color:var(--ink-3)]" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
